/* src/core/stats.ts — frame time, draw calls and triangle count.
 *
 * CLAUDE.md: "Performance is a test, not a vibe." These numbers are asserted in CI against a
 * fixed camera path (TASKS.md 1.10), and surfaced in a `?debug=1` overlay for humans.
 *
 * The frame-time ring buffer is preallocated and never grows — a stats module that allocates
 * per frame would be measuring its own garbage.
 */

export interface StatsSnapshot {
  readonly fps: number;
  /** Wall-clock gap between frames. Vsync-locked, so this is ~16.7 ms whenever fps is 60. */
  readonly frameMsAvg: number;
  readonly frameMsP95: number;
  readonly frameMsMax: number;
  /**
   * Main-thread work per frame: camera update, tile reconciliation, and the render call.
   * This — not the frame gap — is what CLAUDE.md's "< 8 ms at p95 while panning" budget
   * means. Measuring the gap instead would report ~16.7 ms on a perfectly idle 60fps frame
   * and make the budget meaningless.
   */
  readonly cpuMsAvg: number;
  readonly cpuMsP95: number;
  readonly cpuMsMax: number;
  readonly drawCalls: number;
  readonly triangles: number;
  readonly programs: number;
  readonly geometries: number;
  readonly textures: number;
  readonly samples: number;
}

const CAPACITY = 240; // ~4 s at 60fps

export class Stats {
  readonly #frameMs = new Float32Array(CAPACITY);
  readonly #cpuMs = new Float32Array(CAPACITY);
  #count = 0;
  #cursor = 0;
  #lastTime = 0;
  #frameStart = 0;

  drawCalls = 0;
  triangles = 0;
  programs = 0;
  geometries = 0;
  textures = 0;

  /** Call once per frame, before doing any work. Returns delta seconds. */
  beginFrame(now: number): number {
    this.#frameStart = now;
    if (this.#lastTime === 0) {
      this.#lastTime = now;
      return 0;
    }
    const deltaMs = now - this.#lastTime;
    this.#lastTime = now;

    this.#frameMs[this.#cursor] = deltaMs;
    return deltaMs / 1000;
  }

  /**
   * Call at the end of the frame, after render(). Note this measures CPU time to *submit*
   * the frame; the GPU may still be working. That is the right number for a main-thread
   * budget, but it is not a full frame cost — GPU time needs a timer query, which is a
   * Phase 8 concern.
   */
  endFrame(): void {
    this.#cpuMs[this.#cursor] = performance.now() - this.#frameStart;
    this.#cursor = (this.#cursor + 1) % CAPACITY;
    if (this.#count < CAPACITY) this.#count += 1;
  }

  /**
   * Copy the renderer's counters. Structurally typed rather than importing `WebGLInfo`, so
   * this module stays free of a Three.js dependency and can be unit-tested without a GPU.
   * `programs` is an array on WebGLInfo (and null before the first render), so only its
   * length is of interest here.
   */
  setRenderInfo(info: {
    render: { calls: number; triangles: number };
    programs: { length: number } | null;
    memory: { geometries: number; textures: number };
  }): void {
    this.drawCalls = info.render.calls;
    this.triangles = info.render.triangles;
    this.programs = info.programs?.length ?? 0;
    this.geometries = info.memory.geometries;
    this.textures = info.memory.textures;
  }

  reset(): void {
    this.#count = 0;
    this.#cursor = 0;
    this.#lastTime = 0;
  }

  snapshot(): StatsSnapshot {
    if (this.#count === 0) {
      return {
        fps: 0, frameMsAvg: 0, frameMsP95: 0, frameMsMax: 0,
        cpuMsAvg: 0, cpuMsP95: 0, cpuMsMax: 0,
        drawCalls: this.drawCalls, triangles: this.triangles, programs: this.programs,
        geometries: this.geometries, textures: this.textures, samples: 0,
      };
    }

    // Sorting copies of at most 240 floats, and only when someone asks for a snapshot —
    // not per frame.
    const frame = summarise(this.#frameMs, this.#count);
    const cpu = summarise(this.#cpuMs, this.#count);

    return {
      fps: frame.avg > 0 ? 1000 / frame.avg : 0,
      frameMsAvg: frame.avg,
      frameMsP95: frame.p95,
      frameMsMax: frame.max,
      cpuMsAvg: cpu.avg,
      cpuMsP95: cpu.p95,
      cpuMsMax: cpu.max,
      drawCalls: this.drawCalls,
      triangles: this.triangles,
      programs: this.programs,
      geometries: this.geometries,
      textures: this.textures,
      samples: this.#count,
    };
  }
}

function summarise(
  buffer: Float32Array,
  count: number,
): { avg: number; p95: number; max: number } {
  const sorted = Array.from(buffer.subarray(0, count)).sort((a, b) => a - b);
  let sum = 0;
  for (const v of sorted) sum += v;
  const p95Index = Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95));
  return {
    avg: sum / sorted.length,
    p95: sorted[p95Index] ?? 0,
    max: sorted[sorted.length - 1] ?? 0,
  };
}

export const stats = new Stats();

/** A camera pose, for the fixed path CI drives to assert the perf budget (TASKS.md 1.10). */
export interface CameraPose {
  x: number;
  z: number;
  distance: number;
  pitch: number;
  yaw: number;
}

/**
 * Test and CI surface. Playwright drives `__gfCamera` along a fixed path, then reads
 * `__gfStats()` — performance is asserted against a repeatable camera, not whatever pose the
 * page happened to boot into.
 */
declare global {
  interface Window {
    __gfStats?: () => StatsSnapshot;
    __gfStatsReset?: () => void;
    __gfCamera?: (pose: Partial<CameraPose>) => void;
    __gfCameraState?: () => CameraPose;
    __gfTiles?: () => {
      resident: number;
      ready: number;
      triangles: number;
      farResident: number;
      farReady: number;
      /* Pipeline positions, for the perf probes: queued → loading → pending(commit). */
      nearQueued: number;
      nearLoading: number;
      nearPending: number;
      farQueued: number;
      farLoading: number;
      farPending: number;
    };
    /** Jump the world to a lon/lat — the travel window's engine, exposed for tests. */
    __gfTravel?: (lon: number, lat: number) => void;
    __gfOrigin?: () => { lon: number; lat: number };
    /** Which surface owns the frame: the flat city, an interior, or the globe. */
    __gfMode?: () => 'city' | 'interior' | 'globe';
    __gfGlobe?: () => { lon: number; lat: number; d: number } | null;
    /** JS heap (Chrome) + live GPU resource counts, for memory regression checks. */
    __gfMem?: () => { heapMB: number; geometries: number; textures: number };
    /** Survey coverage as the client currently knows it, for tests and debugging. */
    __gfSurveys?: () => { pending: number; fresh: number; known: number };
    /** Dev builds only — the Three.js scene, for inspecting what is actually on screen. */
    __gfScene?: unknown;
    /**
     * The last polled game state, and the price mutation. Used by scripts/verify-loop.mjs to
     * assert the Phase 2 loop end-to-end. Harmless to expose: the read is of state the player
     * already owns, and the write goes through the same server function the UI calls, which
     * derives identity from the JWT and validates ownership itself.
     */
    __gfGameState?: () => unknown;
    __gfSetCartPrice?: (cartId: string, price: number) => Promise<unknown>;
    __gfBuy?: (itemId: string, qty: number, toCart?: string) => Promise<unknown>;
    __gfSell?: (itemId: string, qty: number, fromCart?: string) => Promise<unknown>;
    __gfCheckPlacement?: (lon: number, lat: number) => Promise<unknown>;
    __gfPlaceCart?: (lon: number, lat: number, cuisine?: string) => Promise<unknown>;
    __gfSetCuisine?: (cuisine: string) => Promise<unknown>;
    __gfPrep?: () => Promise<unknown>;
    __gfRestock?: (cartId: string, servings: number) => Promise<unknown>;
    __gfRestockQuote?: (cartId: string, servings: number) => Promise<unknown>;
    __gfAdvance?: (minutes: number) => Promise<unknown>;
    __gfParcelAt?: (lon: number, lat: number) => Promise<unknown>;
    __gfParcelByOsmId?: (osmWayId: number) => Promise<unknown>;
    __gfParcel?: (parcelId: string) => Promise<unknown>;
    __gfForSale?: (
      lon: number,
      lat: number,
      maxPrice?: number,
      limit?: number,
    ) => Promise<unknown>;
    __gfOpenInspector?: (lon: number, lat: number) => Promise<unknown>;
    __gfGrant?: (amount?: number) => Promise<unknown>;
    __gfBuyParcel?: (parcelId: string) => Promise<unknown>;
    __gfSellParcel?: (parcelId: string) => Promise<unknown>;
    /** The fit-out loop: fixtures, shelf stock, deliveries. Same RPCs the UI calls. */
    __gfBuyFixture?: (parcelId: string, kind: string, floor?: number) => Promise<unknown>;
    __gfStockShelf?: (
      parcelId: string,
      fixtureId: string,
      itemId: string,
      price: number,
    ) => Promise<unknown>;
    __gfOrderStock?: (parcelId: string, itemId: string, qty: number) => Promise<unknown>;
    __gfRemoveFixture?: (parcelId: string, fixtureId: string) => Promise<unknown>;
    /** Interior walk mode: enter an owned parcel, ride floors, leave. */
    __gfEnterInterior?: (lon: number, lat: number) => Promise<unknown>;
    __gfInteriorState?: () => unknown;
    __gfSetFloor?: (floor: number) => void;
    __gfExitInterior?: () => void;
    /** Interior first-person pose, for asserting that walking moves the camera. Null when out. */
    __gfInteriorPose?: () => {
      x: number;
      y: number;
      z: number;
      yaw: number;
      pitch: number;
      floor: number;
    } | null;
    /** The fixture id under the interior crosshair, or null. */
    __gfInteriorAim?: () => string | null;
    /** Aim the first-person camera — automation cannot drive pointer lock. */
    __gfInteriorLook?: (yaw: number, pitch: number) => void;
    /** Stand the player at an interior-local point — for stair/door tests. */
    __gfInteriorTeleport?: (x: number, z: number) => void;
    /** Mesh vertex counts, slab orientation, stair/door layout — did the room build right? */
    __gfInteriorDebug?: () => Record<string, unknown>;
  }
}

export function exposeStatsForTests(): void {
  window.__gfStats = () => stats.snapshot();
  window.__gfStatsReset = () => {
    stats.reset();
  };
}
